import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { getRecipeById, deleteRecipe, generatePersonalizedSummary } from '../services/api';
import { useAuth } from '../context/authContext';
import RecipeAnalysis from '../components/RecipeAnalysis';
import RecipeStats from '../components/RecipeStats';
import '../styles/RecipeDetails.css';
import '../styles/modal.css';

const RecipeDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [personalizedSummary, setPersonalizedSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [editMode, setEditMode] = useState({
        ingredients: false,
        prerequisites: false,
        steps: false
    });
    const [editData, setEditData] = useState({
        ingredients: [],
        prerequisites: [],
        steps: []
    });
    const [comments, setComments] = useState({
        ingredients: {},
        prerequisites: {},
        steps: {},
        instructions: {}
    });
    const [newComment, setNewComment] = useState('');
    const [commentSection, setCommentSection] = useState('');
    const [commentIndex, setCommentIndex] = useState(-1);
    const [checkedIngredients, setCheckedIngredients] = useState(new Set());

    // Determine if this is overview mode (from explore page) or full mode
    const isOverviewMode = location.state?.from === 'explore' || 
                          (!user || (recipe && recipe.user_id !== user?.uid));

    const getCuisineBadgeClass = (category) => {
        if (!category) return 'badge-default';
        return `badge-${category.toLowerCase()}`;
    };

    const formatCookingTime = (minutes) => {
        if (!minutes) return 'No time specified';
        if (minutes < 60) return `${minutes}m`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    };

    const fetchPersonalizedSummary = async () => {
        if (!user || !recipe) return;
        
        try {
            setSummaryLoading(true);
            const token = await user.getIdToken();
            const summary = await generatePersonalizedSummary(recipe.id, token);
            setPersonalizedSummary(summary);
        } catch (err) {
            console.error('Error fetching personalized summary:', err);
            // Don't set error state for summary failure, just log it
        } finally {
            setSummaryLoading(false);
        }
    };

    useEffect(() => {
        const fetchRecipeDetails = async () => {
            try {
                setLoading(true);
                const recipeData = await getRecipeById(id);

                // Ensure ingredients is an array
                if (typeof recipeData.ingredients === 'string') {
                    recipeData.ingredients = JSON.parse(recipeData.ingredients);
                }

                // Ensure instructions are parsed if necessary
                if (typeof recipeData.instructions === 'string') {
                    recipeData.instructions = JSON.parse(recipeData.instructions);
                }

                // Ensure prerequisites and steps are arrays
                const instructions = recipeData.instructions || {};
                instructions.prerequisites = Array.isArray(instructions.prerequisites)
                    ? instructions.prerequisites
                    : instructions.prerequisites ? instructions.prerequisites.split('\n') : [];
                instructions.steps = Array.isArray(instructions.steps)
                    ? instructions.steps
                    : instructions.steps ? instructions.steps.split('\n') : [];

                setRecipe(recipeData);
                setEditData({
                    ingredients: [...recipeData.ingredients],
                    prerequisites: [...instructions.prerequisites],
                    steps: [...instructions.steps]
                });
            } catch (err) {
                console.error('Error fetching recipe details:', err);
                setError('Failed to load recipe details. Please try again later.');
            } finally {
                setLoading(false);
            }
        };
        fetchRecipeDetails();
    }, [id]);

    // Fetch personalized summary when recipe is loaded and user is available
    useEffect(() => {
        if (recipe && user) {
            fetchPersonalizedSummary();
        }
    }, [recipe, user]);

    const toggleEdit = (section) => {
        setEditMode(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleEditChange = (section, index, value) => {
        setEditData(prev => ({
            ...prev,
            [section]: prev[section].map((item, i) => i === index ? value : item)
        }));
    };

    const addEditItem = (section) => {
        setEditData(prev => ({
            ...prev,
            [section]: [...prev[section], '']
        }));
    };

    const removeEditItem = (section, index) => {
        if (editData[section].length > 1) {
            setEditData(prev => ({
                ...prev,
                [section]: prev[section].filter((_, i) => i !== index)
            }));
        }
    };

    const saveEdit = async (section) => {
        // Here you would typically make an API call to save the changes
        // For now, we'll just update the local state
        const updatedRecipe = { ...recipe };
        if (section === 'ingredients') {
            updatedRecipe.ingredients = editData.ingredients.filter(item => item.trim());
        } else {
            updatedRecipe.instructions[section] = editData[section].filter(item => item.trim());
        }
        setRecipe(updatedRecipe);
        setEditMode(prev => ({ ...prev, [section]: false }));
    };

    const cancelEdit = (section) => {
        // Reset edit data to original values
        if (section === 'ingredients') {
            setEditData(prev => ({ ...prev, ingredients: [...recipe.ingredients] }));
        } else {
            setEditData(prev => ({ ...prev, [section]: [...recipe.instructions[section]] }));
        }
        setEditMode(prev => ({ ...prev, [section]: false }));
    };

    const toggleIngredientCheck = (index) => {
        setCheckedIngredients(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const addComment = (section, index = -1) => {
        if (!newComment.trim()) return;
        
        let commentKey;
        if (section === 'instructions') {
            commentKey = 'instructions';
        } else {
            commentKey = index >= 0 ? `${section}_${index}` : section;
        }
        
        setComments(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [commentKey]: [...(prev[section][commentKey] || []), {
                    text: newComment,
                    author: user?.displayName || user?.email || 'Unknown User',
                    timestamp: new Date().toISOString()
                }]
            }
        }));
        setNewComment('');
        setCommentSection('');
        setCommentIndex(-1);
    };

    const deleteComment = (section, commentKey, commentIndex) => {
        setComments(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [commentKey]: prev[section][commentKey].filter((_, index) => index !== commentIndex)
            }
        }));
    };

    const handleFork = () => {
        if (!user) {
            navigate('/login');
            return;
        }
        navigate(`/fork/${id}`, { state: { recipe } });
    };

    const handleEdit = () => {
        navigate(`/edit/${id}`);
    };

    const handleDelete = async () => {
        if (!user) return;
        if (window.confirm(`Are you sure you want to delete "${recipe.name}"?`)) {
            try {
                const token = await user.getIdToken();
                await deleteRecipe(id, token);
                navigate('/my-recipes');
            } catch (err) {
                console.error('Error deleting recipe:', err);
                setError('Failed to delete recipe. Please try again.');
            }
        }
    };

    if (loading) return <div className="text-center py-2">Loading…</div>;
    if (error) return <div className="error-message compact">{error}</div>;
    if (!recipe) return <div className="text-center py-2">Recipe not found.</div>;

    const isOwner = user && recipe.user_id === user.uid;

    return (
        <div className={`recipe-details-page ${isOverviewMode ? 'overview-mode' : ''}`}>
            {loading && (
                <div className="text-center py-2">
                    <h2>Loading recipe details…</h2>
                </div>
            )}

            {error && <div className="error-message compact">{error}</div>}

            {recipe && (
                <>
                    {/* Recipe Header */}
                    <div className="recipe-header">
                        <h1 className="recipe-title">{recipe.name}</h1>
                        
                        <div className="recipe-meta">
                            <span>By: {recipe.author_name || 'Unknown'}</span>
                            {recipe.category && (
                                <span className={`recipe-category ${getCuisineBadgeClass(recipe.category)}`}>
                                    {recipe.category}
                                </span>
                            )}
                        </div>

                        {/* Single Action Buttons Section - Fixed positioning */}
                        <div className="recipe-action-buttons">
                            {user && recipe.user_id === user.uid ? (
                                // Owner buttons
                                <>
                                    <button onClick={handleEdit} className="btn-edit-small">
                                        Edit
                                    </button>
                                    <button onClick={handleDelete} className="btn-edit-small btn-danger">
                                        Delete
                                    </button>
                                </>
                            ) : (
                                // Non-owner button (fork)
                                <button onClick={handleFork} className="btn-edit-small">
                                    🍴 Fork This Recipe
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Main recipe image */}
                    {recipe.images && recipe.images.length > 0 && (
                        <div className="recipe-main-image-container">
                            <img 
                                src={recipe.images[0]} 
                                alt={recipe.name}
                                className="recipe-main-image"
                            />
                        </div>
                    )}

                    {/* Recipe Stats */}
                    <RecipeStats recipe={recipe} />

                    {/* Recipe Content */}
                    <div className="recipe-content">
                        {/* Blog-post style description for overview mode */}
                        {isOverviewMode && (
                            <div className="recipe-section">
                                <h3>{recipe.name}</h3>
                                
                                {summaryLoading ? (
                                    <div className="ai-loading-message">
                                        <div className="loading-spinner"></div>
                                        <p>Analyzing recipe for you...</p>
                                    </div>
                                ) : personalizedSummary ? (
                                    <div className="personalized-summary">
                                        <p dangerouslySetInnerHTML={{ __html: personalizedSummary.summary }}></p>
                                        {personalizedSummary.allergyWarning && (
                                            <div className="allergy-warning" style={{
                                                backgroundColor: '#fff5f5',
                                                border: '1px solid #fed7d7',
                                                borderRadius: '4px',
                                                padding: 'var(--spacing-md)',
                                                marginTop: 'var(--spacing-md)',
                                                color: '#c53030'
                                            }}>
                                                <strong>⚠️ Allergy Alert:</strong> {personalizedSummary.allergyWarning}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p>
                                        If you're looking for a simple way to enjoy <strong>{recipe.name}</strong>, this{' '}
                                        <strong>{recipe.category}</strong> recipe delivers a comforting, home‐style result every time.
                                        You'll need basic equipment like a cutting board, mixing bowls, and a medium saucepan.
                                    </p>
                                )}
                                
                                <div style={{ textAlign: 'center', marginTop: 'var(--spacing-md)' }}>
                                    <button onClick={handleFork} className="btn-small">
                                        🍴 Fork This Recipe
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Full mode content - Ingredients Section */}
                        {!isOverviewMode && (
                            <>
                                <div className="recipe-section">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h3>Ingredients</h3>
                                        {isOwner && (
                                            <button 
                                                onClick={() => toggleEdit('ingredients')} 
                                                className="btn-edit-small"
                                                title={editMode.ingredients ? 'Cancel' : 'Edit Ingredients'}
                                            >
                                                {editMode.ingredients ? '✕' : '✎'}
                                            </button>
                                        )}
                                    </div>

                                    {editMode.ingredients ? (
                                        <div className="dynamic-list compact">
                                            {editData.ingredients.map((ingredient, index) => (
                                                <div key={index} className="list-item compact">
                                                    <span style={{minWidth: '2rem', color: 'var(--color-medium-gray)'}}>{index + 1}.</span>
                                                    <input
                                                        type="text"
                                                        value={ingredient}
                                                        onChange={(e) => handleEditChange('ingredients', index, e.target.value)}
                                                        placeholder="Enter ingredient..."
                                                    />
                                                    {editData.ingredients.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="remove-item-btn"
                                                            onClick={() => removeEditItem('ingredients', index)}
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                            <button
                                                type="button"
                                                className="add-item-btn small-text"
                                                onClick={() => addEditItem('ingredients')}
                                            >
                                                + Add Ingredient
                                            </button>
                                            <div className="d-flex gap-1 mt-1">
                                                <button onClick={() => saveEdit('ingredients')} className="btn-small btn-success">
                                                    💾 Save
                                                </button>
                                                <button onClick={() => cancelEdit('ingredients')} className="btn-small btn-secondary">
                                                    Cancel
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <ul className="ingredients-list compact">
                                            {recipe.ingredients.map((ingredient, index) => (
                                                <li key={index} style={{textDecoration: checkedIngredients.has(index) ? 'line-through' : 'none', opacity: checkedIngredients.has(index) ? 0.6 : 1}}>
                                                    <input
                                                        type="checkbox"
                                                        className="ingredient-checkbox"
                                                        checked={checkedIngredients.has(index)}
                                                        onChange={() => toggleIngredientCheck(index)}
                                                    />
                                                    {ingredient}
                                                </li>
                                            ))}
                                        </ul>
                                    )}

                                    <button
                                        onClick={() => { setCommentSection('ingredients'); setCommentIndex(-1); }}
                                        className="btn-comment-accent"
                                    >
                                        Comment
                                    </button>

                                    {/* Section-level comments */}
                                    {comments.ingredients && comments.ingredients.ingredients && (
                                        <div className="comment-block">
                                            <strong>Comments:</strong>
                                            {comments.ingredients.ingredients.map((comment, idx) => (
                                                <div key={idx} className="comment-item">
                                                    <div className="comment-content">
                                                    <strong>{comment.author}:</strong> {comment.text}
                                                        {user && (user.displayName === comment.author || user.email === comment.author) && (
                                                            <button
                                                                onClick={() => deleteComment('ingredients', 'ingredients', idx)}
                                                                className="comment-delete-btn"
                                                                title="Delete comment"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Instructions Section */}
                                <div className="recipe-section">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <h3>Instructions</h3>
                                    </div>

                                    {/* Prerequisites */}
                                    <div className="mb-1">
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <h4>Prerequisites</h4>
                                            {isOwner && (
                                                <button 
                                                    onClick={() => toggleEdit('prerequisites')} 
                                                    className="btn-edit-small"
                                                    title={editMode.prerequisites ? 'Cancel' : 'Edit Prerequisites'}
                                                >
                                                    {editMode.prerequisites ? '✕' : '✎'}
                                                </button>
                                            )}
                                        </div>

                                        {editMode.prerequisites ? (
                                            <div className="dynamic-list compact">
                                                {editData.prerequisites.map((prerequisite, index) => (
                                                    <div key={index} className="list-item compact">
                                                        <span style={{minWidth: '2rem', color: 'var(--color-medium-gray)'}}>{index + 1}.</span>
                                                        <input
                                                            type="text"
                                                            value={prerequisite}
                                                            onChange={(e) => handleEditChange('prerequisites', index, e.target.value)}
                                                            placeholder="Enter prerequisite..."
                                                        />
                                                        {editData.prerequisites.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="remove-item-btn"
                                                                onClick={() => removeEditItem('prerequisites', index)}
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    className="add-item-btn small-text"
                                                    onClick={() => addEditItem('prerequisites')}
                                                >
                                                    + Add Prerequisite
                                                </button>
                                                <div className="d-flex gap-1 mt-1">
                                                    <button onClick={() => saveEdit('prerequisites')} className="btn-small btn-success">
                                                        💾 Save
                                                    </button>
                                                    <button onClick={() => cancelEdit('prerequisites')} className="btn-small btn-secondary">
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <ul className="instructions-list compact" style={{counterReset: 'step-counter'}}>
                                                {recipe.instructions.prerequisites.map((prerequisite, index) => (
                                                    <li key={index}>
                                                        {prerequisite}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    {/* Cooking Steps */}
                                    <div>
                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                            <h4>Cooking Steps</h4>
                                            {isOwner && (
                                                <button 
                                                    onClick={() => toggleEdit('steps')} 
                                                    className="btn-edit-small"
                                                    title={editMode.steps ? 'Cancel' : 'Edit Steps'}
                                                >
                                                    {editMode.steps ? '✕' : '✎'}
                                                </button>
                                            )}
                                        </div>

                                        {editMode.steps ? (
                                            <div className="dynamic-list compact">
                                                {editData.steps.map((step, index) => (
                                                    <div key={index} className="list-item compact">
                                                        <span style={{minWidth: '2rem', color: 'var(--color-medium-gray)'}}>{index + 1}.</span>
                                                        <input
                                                            type="text"
                                                            value={step}
                                                            onChange={(e) => handleEditChange('steps', index, e.target.value)}
                                                            placeholder="Enter cooking step..."
                                                        />
                                                        {editData.steps.length > 1 && (
                                                            <button
                                                                type="button"
                                                                className="remove-item-btn"
                                                                onClick={() => removeEditItem('steps', index)}
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                                <button
                                                    type="button"
                                                    className="add-item-btn small-text"
                                                    onClick={() => addEditItem('steps')}
                                                >
                                                    + Add Step
                                                </button>
                                                <div className="d-flex gap-1 mt-1">
                                                    <button onClick={() => saveEdit('steps')} className="btn-small btn-success">
                                                        💾 Save
                                                    </button>
                                                    <button onClick={() => cancelEdit('steps')} className="btn-small btn-secondary">
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <ul className="instructions-list compact">
                                                {recipe.instructions.steps.map((step, index) => (
                                                    <li key={index}>
                                                        {step}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => { setCommentSection('instructions'); setCommentIndex(-1); }}
                                        className="btn-comment-accent"
                                    >
                                        Comment
                                    </button>

                                    {/* Section-level comments */}
                                    {comments.instructions && comments.instructions.instructions && (
                                        <div className="comment-block">
                                            <strong>Comments:</strong>
                                            {comments.instructions.instructions.map((comment, idx) => (
                                                <div key={idx} className="comment-item">
                                                    <div className="comment-content">
                                                        <strong>{comment.author}:</strong> {comment.text}
                                                        {user && (user.displayName === comment.author || user.email === comment.author) && (
                                                            <button
                                                                onClick={() => deleteComment('instructions', 'instructions', idx)}
                                                                className="comment-delete-btn"
                                                                title="Delete comment"
                                                            >
                                                                ✕
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Comment Input Modal - Only in full mode */}
                    {!isOverviewMode && commentSection && (
                        <div className="comment-modal-overlay">
                            <div className="comment-modal">
                                <h3>Add Comment</h3>
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Enter your comment..."
                                    className="comment-textarea"
                                />
                                <div className="d-flex gap-1">
                                    <button onClick={() => addComment(commentSection, commentIndex)} className="btn-comment-accent">
                                        Add Comment
                                    </button>
                                    <button onClick={() => {setCommentSection(''); setCommentIndex(-1); setNewComment('');}} className="btn-comment-accent">
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* AI Analysis Section */}
                    <RecipeAnalysis recipe={recipe} />
                </>
            )}
        </div>
    );
};

export default RecipeDetails; 